import React, { useState, ChangeEvent, FormEvent } from 'react';
import {
  Container,
  Button,
  Typography,
  Box,
  CircularProgress,
  TextField,
  Grid,
} from '@mui/material';
import propertyService from '../services/propertyService';
import { WorkItem as WorkItemType } from '../models/workItem';
import QuoteDetails from '../components/WorkItemComponents/QuoteComponents/QuoteDetails';

const QuoteAnalysis: React.FC = () => {
  const [zipCode, setZipCode] = useState('');
  const [squareFootage, setSquareFootage] = useState<number | null>(null);
  const [workItemDescription, setWorkItemDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [analysisMessage, setAnalysisMessage] = useState<string | null>(null);
  const [workItems, setWorkItems] = useState<any[]>([]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!zipCode || !squareFootage || !workItemDescription || !file) {
      setAnalysisMessage('Please fill in all fields and upload a file.');
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating property record...');

    try {
      // Step 1: Create the property record
      const propertyId = await propertyService.createProperty("", "", "", zipCode); // TODO: Replace with actual address

      if (!propertyId) {
        throw new Error('Failed to create property record.');
      }

      setLoadingMessage('Adding work item to property...');

      await propertyService.updateProperty(propertyId, {
        id: propertyId,
        address: zipCode,
        squareFootage: squareFootage,
        imageIds: [],
        inspectionRecords: [],
        workItems: [],
      });

      // Step 2: Add a work item to the property
      const workItem: WorkItemType = {
        id: '',
        description: workItemDescription,
        type: 'REPAIR',
        estimatedCostLow: 0,
        estimatedCostHigh: 0,
        status: 'NOT STARTED',
      };

      const workItemReturn = await propertyService.createWorkItem(propertyId, workItem);

      if (!workItemReturn) {
        throw new Error('Failed to create work item.');
      }

      setLoadingMessage('Uploading and analyzing quote...');

      // Step 3: Upload the quote and analyze it
      const analysisResult = await propertyService.createQuoteAnalysisRecord(
        propertyId,
        workItemReturn.id,
        file
      );

      if (!analysisResult) {
        throw new Error('Failed to upload and analyze the quote.');
      }

      setWorkItems((prev) => [
        ...prev,
        {
          ...workItem,
          id: workItemReturn.id,
          quote: {
            quoteId: analysisResult.quoteId,
            analysis: analysisResult,
          },
        },
      ]);

      setAnalysisMessage('Quote analysis completed successfully.');
    } catch (error) {
      console.error('Error during workflow:', error);
      setAnalysisMessage('An error occurred during the analysis workflow.');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleReset = () => {
    setZipCode('');
    setSquareFootage(null);
    setWorkItemDescription('');
    setFile(null);
    setWorkItems([]);
    setAnalysisMessage(null);
  };

  return (
    <Container component="main" maxWidth="md" sx={{ mt: 5 }}>
      <Typography component="h1" variant="h5" gutterBottom>
        Quote Analyzer
      </Typography>

      {/* Show Form if no work items exist */}
      {workItems.length === 0 ? (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ mt: 4, width: '100%' }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Zip Code"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                fullWidth
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Square Footage"
                type="number"
                value={squareFootage ?? ''}
                onChange={(e) => setSquareFootage(Number(e.target.value))}
                fullWidth
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Work Item Description"
                value={workItemDescription}
                onChange={(e) => setWorkItemDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" component="label" fullWidth disabled={loading}>
                Select Quote File
                <input type="file" hidden onChange={handleFileChange} />
              </Button>
              {file && <Typography variant="body2">Selected file: {file.name}</Typography>}
            </Grid>
          </Grid>

          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 4,
              }}
            >
              <CircularProgress size={24} />
              <Typography variant="body1" sx={{ ml: 2 }}>
                {loadingMessage}
              </Typography>
            </Box>
          ) : (
            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 4 }}>
              Submit Analysis
            </Button>
          )}

          {analysisMessage && (
            <Typography
              variant="body2"
              color="error"
              align="center"
              sx={{ mt: 2 }}
            >
              {analysisMessage}
            </Typography>
          )}
        </Box>
      ) : (
        <>
          {/* Show Quote Details */}
          {workItems.map((item) => (
            <QuoteDetails key={item.id} quote={item.quote} />
          ))}
          {/* Reset Button */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
            >
              Clear and Start Over
            </Button>
          </Box>
        </>
      )}
    </Container>
  );
};

export default QuoteAnalysis;
